/**
 * Quote-feasibility panel — SA-268.
 *
 * Shows the current feasibility decision, the scenario/option lineage, and
 * revision history for a quote-class order. Self-fetches its state on mount
 * via `getFeasibility`; renders nothing for non-quote orders. Data methods
 * (getFeasibility/recordFeasibility/selectScenario) come from useOrders as
 * props — the panel owns no fetch plumbing of its own, only its form/UI
 * state, mirroring how OrderDetail's other sections take `on*` callbacks.
 */

import { useState, useEffect, useCallback } from 'react'
import { cn } from '@/utils'
import { RecordSection } from '@/components/records/RecordSection'
import { InnerDisclosure } from '@/components/records/InnerDisclosure'
import { feasibilitySummary } from '@/components/records/sectionSummaries'
import { recordedBeforeWait, type FeasibilityAwait } from '@/components/records/feasibilityAwait'
import { Badge } from '@/components/ui'
import {
  ADDS_A_RESULT, DECISION_LABELS, DECISION_COLORS, RECORD_ANOTHER_RESULT, RECORD_RESULT, SAVE_RESULT,
  SCENARIO_STATUS_LABELS, SCENARIO_STATUS_COLORS, awaitedChip, awaitedLine, recordedBeforeLine,
} from './feasibilityText'
import type {
  DecisionState, OrderFeasibilityState, FeasibilityPayload,
} from '@/types/commerce'

interface OptionRow {
  label: string
  detailsNote: string
}

const EMPTY_OPTION: OptionRow = { label: '', detailsNote: '' }

interface FeasibilityPanelProps {
  orderId: string
  orderType: string
  outcome?: string | null
  getFeasibility: (orderId: string) => Promise<OrderFeasibilityState>
  recordFeasibility: (orderId: string, payload: FeasibilityPayload) => Promise<OrderFeasibilityState>
  selectScenario: (orderId: string, scenarioId: string) => Promise<OrderFeasibilityState>
  /** `cards` (default): today's three boxes — the form frame and the inbox
   * sidebar. `section`: the record lane's one Feasibility section with the
   * Options and Revision history folded inside (SA-461, spec §7.4). */
  chrome?: 'cards' | 'section'
  /** `?panel=feasibility` (section chrome only). */
  focused?: boolean
  /** SA-1026: called after every write that moved the record (a recorded
   * result, a scenario select), once the panel holds the response. The
   * record page passes `onRecordChanged`, so the detail refetches and the
   * process reads (band, strip, Tasks, task page) re-read the step the
   * write advanced; without it they sat on the app's five-minute staleTime. */
  onChanged?: () => void
  /** SA-1076 (section chrome only): the record's process waits on a
   * feasibility result, so the section says a result here ends that step. */
  awaiting?: FeasibilityAwait | null
  /** SA-1077 (Q1): the lane's default; the form frame and the Inbox sidebar pass nothing (today's open). */
  defaultOpen?: boolean
  /** SA-1418 (R1, section chrome only): the record whose active task is the feasibility wait, else null. */
  activeTaskFor?: string | null
}

export function FeasibilityPanel({
  orderId, orderType, outcome, getFeasibility, recordFeasibility, selectScenario,
  chrome = 'cards', focused = false, onChanged, awaiting = null, defaultOpen = true, activeTaskFor = null,
}: FeasibilityPanelProps) {
  const [state, setState] = useState<OrderFeasibilityState | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [formDecisionState, setFormDecisionState] = useState<DecisionState>('unverified')
  const [formLimitingFactor, setFormLimitingFactor] = useState('')
  const [formNote, setFormNote] = useState('')
  const [formOptions, setFormOptions] = useState<OptionRow[]>([{ ...EMPTY_OPTION }])
  const [submitting, setSubmitting] = useState(false)
  const [scenarioActionId, setScenarioActionId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const next = await getFeasibility(orderId)
      setState(next)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setIsLoading(false)
    }
  }, [orderId, getFeasibility])

  useEffect(() => {
    if (orderType !== 'quote') return
    load()
    // load() is stable per [orderId, getFeasibility]; re-running on every
    // load() identity change would refetch after every record/select too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orderType])

  if (orderType !== 'quote') return null

  const closed = Boolean(outcome)

  const resetForm = () => {
    setFormDecisionState('unverified')
    setFormLimitingFactor('')
    setFormNote('')
    setFormOptions([{ ...EMPTY_OPTION }])
  }

  // SA-1041: a new result starts from the one shown, not from Unverified.
  const openForm = () => {
    setFormDecisionState(state?.feasibility?.decisionState ?? 'unverified')
    setShowForm(true)
  }

  const handleAddOption = () => setFormOptions(prev => [...prev, { ...EMPTY_OPTION }])
  const handleRemoveOption = (i: number) =>
    setFormOptions(prev => prev.filter((_, idx) => idx !== i))
  const handleOptionChange = (i: number, field: keyof OptionRow, value: string) =>
    setFormOptions(prev => prev.map((o, idx) => (idx === i ? { ...o, [field]: value } : o)))

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const payload: FeasibilityPayload = {
        decisionState: formDecisionState,
        limitingFactor: formLimitingFactor.trim() || undefined,
        note: formNote.trim() || undefined,
        options: formOptions
          .filter(o => o.label.trim())
          .map(o => ({
            label: o.label.trim(),
            details: o.detailsNote.trim() ? { note: o.detailsNote.trim() } : undefined,
          })),
      }
      const next = await recordFeasibility(orderId, payload)
      setState(next)
      resetForm()
      setShowForm(false)
      onChanged?.()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSelect = async (scenarioId: string) => {
    setScenarioActionId(scenarioId)
    setError(null)
    try {
      const next = await selectScenario(orderId, scenarioId)
      setState(next)
      onChanged?.()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setScenarioActionId(null)
    }
  }

  const feasibility = state?.feasibility ?? null
  const scenarios = [...(state?.scenarios ?? [])].sort((a, b) => a.scenarioNumber - b.scenarioNumber)
  const revisions = [...(state?.revisions ?? [])].sort((a, b) => a.revisionNumber - b.revisionNumber)

  const decisionBody = feasibility ? (
    <div className="space-y-1">
      <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', DECISION_COLORS[feasibility.decisionState])}>
        {DECISION_LABELS[feasibility.decisionState]}
      </span>
      {feasibility.limitingFactor && (
        <p className="text-sm text-muted-foreground">Limiting factor: {feasibility.limitingFactor}</p>
      )}
      {feasibility.note && <p className="text-sm text-foreground">{feasibility.note}</p>}
      {feasibility.recordedAt && (
        <p className="text-xs text-muted-foreground">Recorded {new Date(feasibility.recordedAt).toLocaleString()}</p>
      )}
    </div>
  ) : (
    <p className="text-sm text-muted-foreground">No feasibility recorded yet.</p>
  )

  // SA-1076: the cue is the record lane's (section chrome); the form frame
  // and the Inbox sidebar render as before.
  const cue = chrome === 'section' ? awaiting : null

  const formBody = !closed && (
    <div className="mt-3">
      {!showForm ? (
        <button onClick={openForm}
          data-variant={cue ? 'primary' : undefined}
          className={cn(
            'px-3 py-1.5 text-sm rounded-sm',
            cue ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted text-foreground hover:bg-muted-foreground/10',
          )}>
          {feasibility ? RECORD_ANOTHER_RESULT : RECORD_RESULT}
        </button>
      ) : (
        <div className="space-y-2 border-t border-border pt-3">
          {feasibility && <p className="text-xs text-muted-foreground">{ADDS_A_RESULT}</p>}
          <select
            aria-label="Decision state"
            value={formDecisionState}
            onChange={e => setFormDecisionState(e.target.value as DecisionState)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            {(Object.keys(DECISION_LABELS) as DecisionState[]).map(ds => (
              <option key={ds} value={ds}>{DECISION_LABELS[ds]}</option>
            ))}
          </select>
          <input
            aria-label="Limiting factor"
            type="text"
            value={formLimitingFactor}
            onChange={e => setFormLimitingFactor(e.target.value)}
            placeholder="Limiting factor (optional)"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <textarea
            aria-label="Note"
            value={formNote}
            onChange={e => setFormNote(e.target.value)}
            placeholder="Note (optional)"
            rows={2}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Options</p>
            {formOptions.map((opt, i) => (
              <div key={i} className="flex gap-2 items-start">
                <input
                  aria-label={`Option ${i + 1} label`}
                  type="text"
                  value={opt.label}
                  onChange={e => handleOptionChange(i, 'label', e.target.value)}
                  placeholder="Label"
                  className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                />
                <input
                  aria-label={`Option ${i + 1} details`}
                  type="text"
                  value={opt.detailsNote}
                  onChange={e => handleOptionChange(i, 'detailsNote', e.target.value)}
                  placeholder="Details (optional)"
                  className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                />
                {formOptions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(i)}
                    className="px-2 py-1.5 text-xs text-muted-foreground hover:text-destructive"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={handleAddOption} className="px-2 py-1 text-xs text-primary hover:text-primary/80">
              + Add option
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50"
            >
              {submitting ? 'Saving…' : SAVE_RESULT}
            </button>
            <button
              onClick={() => { setShowForm(false); resetForm() }}
              className="px-3 py-1.5 text-sm text-muted-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )

  const optionsBody = scenarios.length === 0 ? (
    <p className="text-sm text-muted-foreground">No options recorded yet.</p>
  ) : (
    <ul className="space-y-2">
      {scenarios.map(s => (
        <li key={s.id} className="flex items-center justify-between text-sm gap-2">
          <div>
            <span className="text-foreground">{s.label}</span>
            <span className={cn('ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', SCENARIO_STATUS_COLORS[s.status])}>
              {SCENARIO_STATUS_LABELS[s.status]}
            </span>
            {s.decidedAt && (
              <span className="ml-2 text-xs text-muted-foreground">{new Date(s.decidedAt).toLocaleString()}</span>
            )}
          </div>
          {!closed && s.status === 'candidate' && (
            <button
              onClick={() => handleSelect(s.id)}
              disabled={scenarioActionId === s.id}
              className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-sm disabled:opacity-50 shrink-0"
            >
              {scenarioActionId === s.id ? '…' : 'Select'}
            </button>
          )}
        </li>
      ))}
    </ul>
  )

  const revisionsBody = revisions.length === 0 ? (
    <p className="text-sm text-muted-foreground">No revisions recorded yet.</p>
  ) : (
    <ul className="space-y-1">
      {revisions.map(r => (
        <li key={r.id} className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">#{r.revisionNumber}</span>
          {r.reason && <span> — {r.reason}</span>}
          <span> · {r.changedFields?.length ?? 0} changed fields</span>
          {r.createdAt && <span className="text-xs"> · {new Date(r.createdAt).toLocaleString()}</span>}
        </li>
      ))}
    </ul>
  )

  if (chrome === 'section') {
    const before = !!cue && !!feasibility && recordedBeforeWait(feasibility.recordedAt, cue.since)
    return (
      <RecordSection id="feasibility" title="Feasibility" summary={feasibilitySummary(state, isLoading)} defaultOpen={defaultOpen} focused={focused}
        activeTaskFor={activeTaskFor} className={cue ? 'border-primary/60' : undefined}
        actions={cue ? <Badge variant="info-soft" dot="bg-info" className="gap-1.5">{awaitedChip(cue.stepNumber)}</Badge> : undefined}>
        {cue && <p className="mb-3 rounded-md bg-info/10 px-2.5 py-1.5 text-sm text-foreground">{awaitedLine(cue.stepNumber, cue.stepTitle)}</p>}
        {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <>
            {decisionBody}
            {cue && before && <p className="mt-1 text-xs text-warning-strong">{recordedBeforeLine(cue.stepNumber)}</p>}
            {formBody}
          </>
        )}
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
        {!isLoading && (
          <div className="mt-3 space-y-2 border-t border-border pt-3">
            <InnerDisclosure title={`Options (${scenarios.length})`}>{optionsBody}</InnerDisclosure>
            <InnerDisclosure title={`Revision history (${revisions.length})`}>{revisionsBody}</InnerDisclosure>
          </div>
        )}
      </RecordSection>
    )
  }

  return (
    <>
      <div className="bg-card rounded-lg p-4 border border-border">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Feasibility</h3>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {decisionBody}
            {formBody}
          </>
        )}
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
      </div>

      {!isLoading && (
        <div className="bg-card rounded-lg p-4 border border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Options</h3>
          {optionsBody}
        </div>
      )}

      {!isLoading && (
        <div className="bg-card rounded-lg p-4 border border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Revision history</h3>
          {revisionsBody}
        </div>
      )}
    </>
  )
}
